import { NodeViewContextableWrapper } from "@ext/markdown/core/element/NodeViewContextableWrapper";
import { useBlockToolbar } from "@ext/markdown/elements/article/edit/helpers/useBlockToolbar";
import ViewActions from "@ext/markdown/elements/view/edit/components/Helpers/ViewActions";
import { getDisplayPatch, type ViewSettings } from "@ext/markdown/elements/view/models/ViewSettings";
import View from "@ext/markdown/elements/view/render/components/View";
import { useUpdateViewArticle } from "@ext/markdown/elements/view/render/logic/hooks/useUpdateViewArticle";
import { getEditorContext } from "@ext/markdown/elementsUtils/editorContext/EditorContext";
import type { Display } from "@ext/properties/models/display";
import type { NodeViewProps } from "@tiptap/react";
import { useCallback, useMemo, useRef } from "react";

interface ViewComponentProps extends NodeViewProps {
	className?: string;
}

const ViewComponent = (props: ViewComponentProps) => {
	const { node, className, updateAttributes, editor } = props;
	const hoverElementRef = useRef<HTMLDivElement>(null);
	const isEditable = editor.isEditable;

	const settings: ViewSettings = useMemo(
		() => ({
			defs: node.attrs.defs,
			orderby: node.attrs.orderby,
			groupby: node.attrs.groupby,
			select: node.attrs.select,
			display: node.attrs.display,
		}),
		[node.attrs.defs, node.attrs.orderby, node.attrs.groupby, node.attrs.select, node.attrs.display],
	);

	const updateDisplay = useCallback(
		(display: Display) => updateAttributes(getDisplayPatch(settings, display)),
		[settings, updateAttributes],
	);

	const updateViewArticle = useUpdateViewArticle();
	const updateArticle = useCallback(
		(articlePath: string, property: string, value: string, isDelete?: boolean) => {
			// Foreign content: the property would be written into the real article.
			if (getEditorContext(editor).noWrite) return;
			updateViewArticle(articlePath, property, value, isDelete);
		},
		[editor, updateViewArticle],
	);

	useBlockToolbar(
		props,
		isEditable && (
			<ViewActions settings={settings} updateAttributes={updateAttributes} updateDisplay={updateDisplay} />
		),
		{ comment: true },
	);

	return (
		<NodeViewContextableWrapper data-drag-handle props={props} ref={hoverElementRef}>
			<div
				className={`relative flex flex-col rounded-[var(--radius-medium)] select-none [&_.view-filter-row]:flex [&_.view-filter-row]:flex-wrap [&_.view-filter-row]:gap-[0.5em] [&_.view-filter-row]:[word-wrap:break-word] ${className ?? ""}`}
			>
				<View {...settings} commentId={node.attrs.comment?.id} disabled={false} updateArticle={updateArticle} />
			</div>
		</NodeViewContextableWrapper>
	);
};

export default ViewComponent;
